<script setup>
import {
    getCurrentInstance,
    ref,
    watch,
    onMounted,
} from 'vue'
import {
    DriverStore
} from '../pinia/Driver.js'
import {
    useRoute,
    useRouter
} from 'vue-router'
import NavRailItem from './NavRailItem.vue'
import { notifyDialog, notifyDialogError, readDialogError } from '../composables/useDialogNotice'

const {
    proxy
} = getCurrentInstance()
const Driver = DriverStore()
const route = useRoute()
const router = useRouter()

onMounted(() => {
        if (route.path === '/login') {
                if (!route.path.startsWith('/confirm')) {
                        Driver.showDrawer(true)
                }
        }
})

watch(() => route.path, (newPath) => {
    if (newPath === '/login') {
        if (!newPath.startsWith('/confirm')) {
            Driver.showDrawer(true)
        }
    }
})

var Mail = ref("")
var RMail = ref("")
const MailF = ref("")
const tab = ref()
var Password = ref("")
var RPassword = ref("")
var RPassword2 = ref("")
const registerClicked = ref(false)

async function reset() {
    await proxy.$api({ "serviceName": "session", "methodName": "forgot", "parameters": [MailF.value] })
    notifyDialog({
        type: 'success',
        message: 'If this account exists, you will receive a password reset email soon. Check inbox and spam.',
    })
}

async function resendActivationEmail() {
    if (!RMail.value) {
        notifyDialog({ type: 'error', message: 'Please enter an email address first.' })
        return;
    }
    try {
        await proxy.$api({ "serviceName": "session", "methodName": "resendActivation", "parameters": [RMail.value] })
        notifyDialog({
            type: 'success',
            message: 'If this account exists and is not activated yet, you will receive a new activation email soon. Check inbox and spam.',
        })
    } catch (e) {
        notifyDialogError(e, 'Error sending activation email. Please try again.')
    }
}

function login() {
    proxy.$http.post("", JSON.stringify({
        serviceName: "session",
        methodName: "login",
        parameters: [Mail.value, Password.value, ""]
    })).then(response => {
        Driver.setEasharpptr_p(response.data.easharpptr_p)
        Driver.setEasharpptr_u(response.data.easharpptr_u)
        Driver.setPersona(response.data.persona)
        Driver.setLogged(true)
        Driver.showDrawer(false)
        GetUserInfo()
    }).catch(function (e) {
        notifyDialog({ type: 'error', message: readDialogError(e, 'Could not sign in.') })
    })
}

async function register() {
    if (registerClicked.value) return;
    registerClicked.value = true;
    setTimeout(() => {
        registerClicked.value = false;
    }, 5000);

    if (RPassword.value !== RPassword2.value) {
        notifyDialog({ type: 'error', message: 'Passwords do not match.' })
        return;
    }

    const passwordRegex = new RegExp("^(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*()\\-_=+{};:,<.>]).{8,}$");
    if (!passwordRegex.test(RPassword.value)) {
        notifyDialog({ type: 'error', message: 'Password must contain at least 8 characters, including 1 uppercase letter, 1 digit, and 1 special character.' })
        return;
    }

    try {
        const res = await proxy.$api({ "serviceName": "session", "methodName": "registerUser", "parameters": [RMail.value, RPassword.value, ""] });
        notifyDialog({
            type: 'info',
            title: 'Register',
            message: typeof res === 'string' ? res : readDialogError(res, 'Check your email to activate the account.'),
        })
    } catch (e) {
        notifyDialogError(e, 'An error occurred during registration.')
        setTimeout(() => {
            registerClicked.value = false;
        }, 5000);
    }
}

async function GetUserInfo() {
    const res = await proxy.$api({
        serviceName: "session",
        methodName: "GetUserInfo"
    })
    Driver.setPersona(res.persona)
    Driver.setUser(res.user)
    Driver.setCrew(res.crew)
    Driver.setCurrentCrew(res.crew.current)
    Driver.setOthers(res.others)
    Driver.setLogged(true)
    if (Driver.drawerTo) {
        router.push(Driver.drawerTo)
        Driver.drawerTo = ""
    }
}

function disconnect() {
    Driver.setUser({
        'premium': "0"
    })
}
</script>

<template><NavRailItem
        v-if="Driver.logged"
        icon="logout"
        label="Log out"
        variant="danger"
        @click="Driver.disconnect(); disconnect()"
    />
    <NavRailItem v-else icon="login" label="Log in" :active="route.path === '/login'" @click="Driver.showDrawer(true)" />

    <v-dialog transition="dialog-bottom-transition" :width="545" v-model="Driver.drawer" scrollable>
        <v-card>
            <v-toolbar v-if="Driver.drawerTo" color="primary">
                <v-icon>mdi-login</v-icon>
                <v-toolbar-title>You must be connected</v-toolbar-title>
                <v-spacer></v-spacer>
                <template v-slot:extension>
                    <v-tabs v-model="tab" centered>
                        <v-tab value="1"> Sign in </v-tab>
                        <v-tab value="2"> Register </v-tab>
                        <v-tab value="3"> Forgot Password </v-tab>
                    </v-tabs>
                </template>
            </v-toolbar>
            <v-toolbar v-else dark color="primary">
                <v-icon>mdi-login</v-icon>
                <v-toolbar-title>Sign in</v-toolbar-title>
                <v-spacer></v-spacer>
                <template v-slot:extension>
                    <v-tabs v-model="tab" centered>
                        <v-tab value="1"> Sign in </v-tab>
                        <v-tab value="2"> Register </v-tab>
                        <v-tab value="3"> Forgot Password </v-tab>
                    </v-tabs>
                </template>
            </v-toolbar>
            <v-card-text>
                <v-window v-model="tab">
                    <v-window-item value="1">
                        <div class="settings-tab-page auth-panel">
                            <div class="settings-tab-header">
                                <div class="header-icon"><v-icon>mdi-login</v-icon></div>
                                <div class="header-content">
                                    <h3 class="page-title">Sign in</h3>
                                    <p class="page-subtitle">Use your NightRiderz account email and password.</p>
                                </div>
                            </div>
                            <div class="section-list">
                                <div class="section-row">
                                    <div class="section-body">
                                        <v-text-field v-model="Mail" label="Email" prepend-inner-icon="mdi-email" variant="outlined" density="comfortable" hide-details="auto" />
                                        <v-text-field v-model="Password" type="password" label="Password" prepend-inner-icon="mdi-lock" variant="outlined" density="comfortable" hide-details="auto" class="mt-3" />
                                        <v-btn class="auth-submit" variant="tonal" block @click="login"> Sign in </v-btn>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </v-window-item>
                    <v-window-item value="2">
                        <div class="settings-tab-page auth-panel">
                            <div class="settings-tab-header">
                                <div class="header-icon"><v-icon>mdi-account-plus</v-icon></div>
                                <div class="header-content">
                                    <h3 class="page-title">Register</h3>
                                    <p class="page-subtitle">Create an account, then activate it from your email.</p>
                                </div>
                            </div>
                            <div class="section-list">
                                <div class="section-row">
                                    <div class="section-body">
                                        <v-text-field v-model="RMail" label="Email" prepend-inner-icon="mdi-email" variant="outlined" density="comfortable" hide-details="auto" />
                                        <v-text-field v-model="RPassword" type="password" label="Password" prepend-inner-icon="mdi-lock" variant="outlined" density="comfortable" hide-details="auto" class="mt-3" />
                                        <v-text-field v-model="RPassword2" type="password" label="Confirm password" prepend-inner-icon="mdi-lock" variant="outlined" density="comfortable" hide-details="auto" class="mt-3" />
                                        <p class="auth-hint">Password needs 8+ characters, 1 uppercase, 1 digit, and 1 special character.</p>
                                        <div class="auth-actions">
                                            <v-btn variant="outlined" @click="resendActivationEmail"> Resend activation </v-btn>
                                            <v-btn variant="tonal" :disabled="registerClicked" @click="register"> Register </v-btn>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </v-window-item>
                    <v-window-item value="3">
                        <div class="settings-tab-page auth-panel">
                            <div class="settings-tab-header">
                                <div class="header-icon"><v-icon>mdi-lock-open</v-icon></div>
                                <div class="header-content">
                                    <h3 class="page-title">Forgot Password</h3>
                                    <p class="page-subtitle">We'll email a reset link if the account exists.</p>
                                </div>
                            </div>
                            <div class="section-list">
                                <div class="section-row">
                                    <div class="section-body">
                                        <v-text-field v-model="MailF" label="Email" prepend-inner-icon="mdi-email" variant="outlined" density="comfortable" hide-details="auto" />
                                        <v-btn class="auth-submit" variant="tonal" block @click="reset"> Send reset link </v-btn>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </v-window-item>
                </v-window>
            </v-card-text>
        </v-card>
    </v-dialog></template>

<style scoped>
.auth-panel {
  padding-top:4px}
.auth-hint {
  margin:10px 0 14px;
  color:var(--mute);
  font-size:.78rem;
  line-height:1.4}
.auth-actions {
  display:flex;
  flex-wrap:wrap;
  gap:8px;
  justify-content:flex-end;
  margin-top:14px}
.auth-submit {
  margin-top:14px}
:deep(.v-field:has(input:-webkit-autofill)) {
  background:var(--secondary-color)!important}
:deep(.v-field:has(input:-webkit-autofill) .v-field__overlay) {
  opacity:0!important}
:deep(input:-webkit-autofill),
:deep(input:-webkit-autofill:hover),
:deep(input:-webkit-autofill:focus),
:deep(input:-webkit-autofill:active) {
  -webkit-text-fill-color:var(--ink)!important;
  caret-color:var(--ink)!important;
  background-color:transparent!important;
  -webkit-box-shadow:0 0 0 1000px var(--secondary-color) inset!important;
  box-shadow:0 0 0 1000px var(--secondary-color) inset!important;
  transition:background-color 99999s ease-out 0s!important}
</style>
